import { makeFunctionReference } from "convex/server";
import type { GenericId } from "convex/values";

export const COMPONENTISTICA_MAX_BODY_BYTES = 32 * 1024;
export const COMPONENTISTICA_RATE_LIMIT = 120;
export type ComponentisticaProducer = "primoup" | "primolab";
export type OrganizationId = GenericId<"organizations">;
export type ClinicId = GenericId<"companies">;
export type WarehouseId = GenericId<"componentistica_warehouses">;

export type ComponentisticaBaseEvent = {
  version: 1; event_id: string; source: string; record_version: number;
  occurred_at: string; vendor_id: string; warehouse_clinic_id: string;
  warehouse_article_id: string; article_code: string; article_name: string; lot: string | null;
};
export type StockEvent = ComponentisticaBaseEvent & { quantity: number; status: "active" | "removed" };
export type ConsumptionEvent = ComponentisticaBaseEvent & {
  warehouse_request_id: string; approved_quantity: number; status: "approved" | "cancelled";
  requested_at: string; approved_at: string | null; prescription_id: string | null;
  prescription_number: string | null; recipient_clinic_id: string | null; recipient_clinic_name: string | null;
};
export type ComponentisticaEvent = StockEvent | ConsumptionEvent;

function text(value: unknown, max: number, nullable = false): string | null {
  if (nullable && value === null) return null;
  if (typeof value !== "string") throw new Error("invalid_payload");
  const result = value.trim();
  if (!result || result.length > max || [...result].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) throw new Error("invalid_payload");
  return result;
}
function id(value: unknown, nullable = false): string | null {
  if (nullable && value === null) return null;
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value) || value <= 0) throw new Error("invalid_payload");
    return String(value);
  }
  const result = text(value, 160)!;
  if (!/^[A-Za-z0-9][A-Za-z0-9_.:-]*$/.test(result)) throw new Error("invalid_payload");
  return result;
}
function instant(value: unknown, nullable = false): string | null {
  if (nullable && value === null) return null;
  const result = text(value, 40)!;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(result) || !Number.isFinite(Date.parse(result))) throw new Error("invalid_payload");
  // Reject normalized impossible calendar days (e.g. 30 February).
  const datePart = result.slice(0, 10);
  if (new Date(`${datePart}T00:00:00Z`).toISOString().slice(0, 10) !== datePart) throw new Error("invalid_payload");
  return new Date(result).toISOString();
}
export function parseComponentisticaEvent(input: unknown, producer: ComponentisticaProducer):
  { ok: true; payload: ComponentisticaEvent } | { ok: false; error: "invalid_payload" } {
  try {
    if (!input || typeof input !== "object" || Array.isArray(input)) throw new Error();
    const raw = input as Record<string, unknown>;
    const commonKeys = ["version", "event_id", "source", "record_version", "occurred_at", "vendor_id", "warehouse_clinic_id", "warehouse_article_id", "article_code", "article_name", "lot", "status"];
    const extraKeys = producer === "primoup" ? ["quantity"] : ["warehouse_request_id", "approved_quantity", "requested_at", "approved_at", "prescription_id", "prescription_number", "recipient_clinic_id", "recipient_clinic_name"];
    if (Object.keys(raw).some((key) => ![...commonKeys, ...extraKeys].includes(key))) throw new Error();
    if (raw.version !== 1 || !Number.isSafeInteger(raw.record_version) || Number(raw.record_version) <= 0) throw new Error();
    const eventId = id(raw.event_id)!;
    if (eventId.length < 8) throw new Error();
    const base: ComponentisticaBaseEvent = {
      version: 1, event_id: eventId, source: id(raw.source)!, record_version: Number(raw.record_version),
      occurred_at: instant(raw.occurred_at)!, vendor_id: id(raw.vendor_id)!, warehouse_clinic_id: id(raw.warehouse_clinic_id)!,
      warehouse_article_id: id(raw.warehouse_article_id)!, article_code: text(raw.article_code, 160)!, article_name: text(raw.article_name, 300)!, lot: text(raw.lot, 160, true),
    };
    if (producer === "primoup") {
      if (typeof raw.quantity !== "number" || !Number.isFinite(raw.quantity) || Math.abs(raw.quantity) > 1e9 || !["active", "removed"].includes(String(raw.status))) throw new Error();
      return { ok: true, payload: { ...base, quantity: raw.quantity, status: raw.status as StockEvent["status"] } };
    }
    if (typeof raw.approved_quantity !== "number" || !Number.isFinite(raw.approved_quantity) || raw.approved_quantity <= 0 || raw.approved_quantity > 1e9 || !["approved", "cancelled"].includes(String(raw.status))) throw new Error();
    return { ok: true, payload: {
      ...base, warehouse_request_id: id(raw.warehouse_request_id)!, approved_quantity: raw.approved_quantity,
      status: raw.status as ConsumptionEvent["status"], requested_at: instant(raw.requested_at)!, approved_at: instant(raw.approved_at, true),
      prescription_id: id(raw.prescription_id, true), prescription_number: text(raw.prescription_number, 160, true),
      recipient_clinic_id: id(raw.recipient_clinic_id, true), recipient_clinic_name: text(raw.recipient_clinic_name, 300, true),
    } };
  } catch { return { ok: false, error: "invalid_payload" }; }
}

export type ComponentisticaWarehouse = {
  id: WarehouseId; name: string; source: string; vendorId: string; warehouseClinicId: string;
  enabled: boolean; linkPrescriptions: boolean; lastStockAt: number | null; lastConsumptionAt: number | null;
};
export type ComponentisticaContext = { organizations: { id: OrganizationId; name: string }[]; canManage: boolean };
export type ComponentisticaWorkspace = {
  organizationId: OrganizationId; enabled: boolean; warehouses: ComponentisticaWarehouse[];
  clinics: { id: ClinicId; name: string }[]; canViewOrganization: boolean; accessVersion: string;
};
export type ComponentisticaFilters = {
  organizationId: OrganizationId; warehouseId?: WarehouseId; search?: string; lot?: string;
  cursor?: string | null; limit?: number;
};
export type ConsumptionFilters = ComponentisticaFilters & { clinicId?: ClinicId; from?: string; to?: string };
export type StockRow = { id: string; warehouseId: WarehouseId; warehouseName: string; articleId: string; code: string; name: string; lot: string | null; quantity: number; updatedAt: number };
export type ConsumptionRow = Omit<StockRow, "quantity"> & {
  requestId: string; quantity: number; requestedAt: number; approvedAt: number | null;
  prescriptionNumber: string | null; prescriptionId: GenericId<"prescriptions"> | null;
  clinicId: ClinicId; clinicName: string;
};
export type ComponentisticaPage<T> = { rows: T[]; continueCursor: string | null; isDone: boolean };
export type ComponentisticaDiagnostic = {
  id: string; requestId: string; articleId: string; prescriptionNumber: string | null; externalClinicId: string | null;
  issues: ("missing_clinic" | "no_mapping" | "prescription_mismatch" | "ambiguous_prescription" | "missing_article")[];
};
export type ComponentisticaConfiguration = {
  organizations: { id: OrganizationId; name: string; clinics: { id: ClinicId; name: string }[] }[];
  warehouses: ComponentisticaWarehouse[];
  grants: { id: GenericId<"componentistica_grants">; organizationId: OrganizationId; warehouseId: WarehouseId; clinicIds: ClinicId[] | null; enabled: boolean }[];
  mappings: { id: GenericId<"componentistica_clinic_mappings">; source: string; externalClinicId: string; companyId: ClinicId }[];
  credentials: { id: GenericId<"componentistica_credentials">; name: string; warehouseId: WarehouseId; producer: ComponentisticaProducer; preview: string; active: boolean; expiresAt: number | null; lastUsedAt: number | null }[];
  endpointBase: string | null;
};
export type SaveWarehouseArgs = { warehouseId?: WarehouseId; name: string; source: string; vendorId: string; warehouseClinicId: string; enabled: boolean; linkPrescriptions?: boolean };
export type SaveGrantArgs = { organizationId: OrganizationId; warehouseId: WarehouseId; enabled: boolean; clinicIds?: ClinicId[] };
export type SaveClinicMappingArgs = { source: string; externalClinicId: string; companyId: ClinicId };
export type CreateCredentialArgs = { warehouseId: WarehouseId; producer: ComponentisticaProducer; name: string; expiresAt?: number };
export const componentisticaApi = {
  getContext: makeFunctionReference<"query", Record<string, never>, ComponentisticaContext>("componentistica:getContext"),
  getWorkspace: makeFunctionReference<"query", { organizationId: OrganizationId }, ComponentisticaWorkspace>("componentistica:getWorkspace"),
  listStock: makeFunctionReference<"query", ComponentisticaFilters, ComponentisticaPage<StockRow>>("componentistica:listStock"),
  listConsumptions: makeFunctionReference<"query", ConsumptionFilters, ComponentisticaPage<ConsumptionRow>>("componentistica:listConsumptions"),
  getConfiguration: makeFunctionReference<"query", Record<string, never>, ComponentisticaConfiguration>("componentistica:getConfiguration"),
  listDiagnostics: makeFunctionReference<"query", { warehouseId: WarehouseId; cursor?: string | null; limit?: number }, ComponentisticaPage<ComponentisticaDiagnostic>>("componentistica:listDiagnostics"),
  saveWarehouse: makeFunctionReference<"mutation", SaveWarehouseArgs, WarehouseId>("componentistica:saveWarehouse"),
  saveGrant: makeFunctionReference<"mutation", SaveGrantArgs, GenericId<"componentistica_grants">>("componentistica:saveGrant"),
  saveClinicMapping: makeFunctionReference<"mutation", SaveClinicMappingArgs, GenericId<"componentistica_clinic_mappings">>("componentistica:saveClinicMapping"),
  createCredential: makeFunctionReference<"action", CreateCredentialArgs, { id: GenericId<"componentistica_credentials">; token: string }>("componentistica:createCredential"),
  revokeCredential: makeFunctionReference<"mutation", { credentialId: GenericId<"componentistica_credentials"> }, null>("componentistica:revokeCredential"),
};
